import type { FormControl, FormGroup } from '@angular/forms';

import type { GridsterItem } from 'angular-gridster2';
import type { ECharts } from 'echarts/core';

import type { AsFormControlMap } from '@core/common-legacy';
// eslint-disable-next-line boundaries/element-types
import type { Theme } from '@core/theme-util'; // --FIXME
import type { UnitPrecision } from '@core/utils-legacy';
import { VisibilityLevel } from '@core/visibility-types';

import { ReportDateFilter } from './common.types';
import { ReportDashboardComponentKind } from './consts.types';
import { TimeBasedMetricDatePoint } from './metrics.types';
import {
  ReportDashboardComponent,
  ReportDashboardComponentOptions,
  ReportDashboardComponentSettings,
} from './report-component.types';

export const NO_LIMIT_DATA = -1;
export const DEFAULT_DATA_LIMIT = NO_LIMIT_DATA;

export const DASHBOARD_BASE_PATH = 'boards/:dashboardId';

export const REPORTS_DEFAULT_VISIBILITY = {
  adminIds: [],
  level: VisibilityLevel.Private,
  roleIds: [],
};

export type ChartFactory = (
  el: HTMLDivElement | HTMLCanvasElement,
  theme: Theme,
  devicePixelRatio: number,
  locale: string,
  isExport?: boolean,
) => ECharts;

export enum DataInterval {
  Daily = 'daily',
  Weekly = 'weekly',
  Monthly = 'monthly',
  Quarterly = 'quarterly',
}

export enum VisualizationContext {
  Preview = 'preview',
  Library = 'library',
  Builder = 'builder',
  TemplatesLibrary = 'templates-library',
  Export = 'export',
}

export const SortedIntervals = [
  DataInterval.Daily,
  DataInterval.Weekly,
  DataInterval.Monthly,
  DataInterval.Quarterly,
];

export interface DashboardComponentGridsterItem extends GridsterItem {
  localId: string | null;
  refComponentId?: number;
}

export interface GlobalFilterFormValue {
  dateFilter: ReportDateFilter;
  queryFilter: string;
}

export type GlobalFilterForm = FormGroup<{
  dateFilter: FormGroup<AsFormControlMap<GlobalFilterFormValue['dateFilter']>>;
  queryFilter: FormControl<GlobalFilterFormValue['queryFilter']>;
}>;

export const ReturnToLibraryToken = 'ReturnToLibraryToken';
export const ReturnToStartFromScratchToken = 'ReturnToStartFromScratchToken';

export type ReportDashboardsAbilities = {
  canManageReportDashboards: boolean;
  canViewReportDashboards: boolean;
  canExportData: boolean;
  canViewHiredCandidates: boolean;
  canManageRequisitions: boolean;
  canCreateCustomDashboards: boolean;
};

type ReportsRequiredApiParams = {
  metric: ReportDashboardComponent['metric'];
  includeDeletedCandidates?: ReportDashboardComponentOptions['includeDeletedCandidates'];
  includeArchivedJobs?: ReportDashboardComponentOptions['includeArchivedJobs'];
  includeArchivedRequisitions?: ReportDashboardComponentOptions['includeArchivedRequisitions'];
  showAllData: ReportDashboardComponentOptions['showAllData'];
  primaryGroup?: ReportDashboardComponent['primaryGroup'];
  secondaryGroup?: ReportDashboardComponent['secondaryGroup'];
  sortBy?: ReportDashboardComponent['sortBy'];
  sortOrder?: ReportDashboardComponent['sortOrder'];
  filters?: string;
  interval?: ReportDashboardComponentSettings['interval'];
  dateField: ReportDashboardComponent['dateField']['field'];
  dateResource: ReportDashboardComponent['dateField']['resource'];
  unitPrecision?: UnitPrecision | null;
} & Partial<ReportDashboardComponent['dateFilter']>;

export type ReportsNonTimeBasedApiParams = ReportsRequiredApiParams;

export type ReportsTimeBasedApiParams = ReportsRequiredApiParams & {
  metric: 'custom_time_based';
  startPoint: TimeBasedMetricDatePoint;
  endPoint: TimeBasedMetricDatePoint;
};

export type ReportsApiDataParams = ReportsNonTimeBasedApiParams | ReportsTimeBasedApiParams;

export type ReportsVisualizationDataParams = {
  paramsHash: string;
  kind: ReportDashboardComponentKind;
  params: ReportsApiDataParams;
};
